import { Application } from "@hotwired/stimulus";
import { registerControllers } from "stimulus-vite-helpers";
import Alpine from "alpinejs";

import AirDatepicker from "air-datepicker";
import localeEn from "air-datepicker/locale/en";
import localeFr from "air-datepicker/locale/fr";
import "air-datepicker/air-datepicker.css";
import SlimSelect from "slim-select";
import "../vendors/slim-select.css";

window.Alpine = Alpine;

const application = Application.start();
window.SlimSelect = SlimSelect;

const controllers = import.meta.glob("./*_controller.js", { eager: true });
registerControllers(application, controllers);
// Configure Stimulus development experience
application.debug = false;
window.Stimulus = application;

function appendTurboStream(response) {
  return response.text().then((streamHtml) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = streamHtml;
    console.log(Array.from(tempDiv.children));
    Array.from(tempDiv.children).forEach((el) => {
      if (el.tagName === "TURBO-STREAM") {
        document.body.appendChild(el);
      }
    });
    return null;
  });
}
window.appendTurboStream = appendTurboStream;

function getUserLocale() {
  const lang =
    document
      .querySelector("meta[name='current-locale']")
      ?.getAttribute("content") ||
    navigator.language ||
    navigator.userLanguage ||
    "en";
  return lang.startsWith("fr") ? "fr" : "en";
}

// SlimSelect's own texts, in the page's language (owner's remark of 30/09:
// « Select Value » showed wherever a field gave no placeholder).
const SLIM_TEXTS = {
  fr: { multiple: "Choisir un ou plusieurs éléments…", single: "Choisir…", search: "Rechercher",
        searching: "Recherche…", none: "Aucun résultat" },
  en: { multiple: "Choose one or more…", single: "Choose…", search: "Search",
        searching: "Searching…", none: "No results" },
};

function parseLocalDate(isoDate) {
  const [year, month, day] = isoDate.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function toLongDate(date) {
  const userLocale = getUserLocale();
  const formatter = new Intl.DateTimeFormat(
    userLocale === "fr" ? "fr-FR" : "en-US",
    {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }
  );
  return formatter.format(date);
}
window.toLongDate = toLongDate;

function formatDateForServer(date) {
  // 2025-10-19
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}
window.formatDateForServer = formatDateForServer;

function formatDateForInput(date) {
  const userLocale = getUserLocale();
  const formatter = new Intl.DateTimeFormat(
    userLocale === "fr" ? "fr-FR" : "en-US",
    {
      day: "2-digit",
      month: "long",
      year: "numeric",
    }
  );
  return formatter.format(date);
}
window.formatDateForInput = formatDateForInput;

function updateDatepickerValue(selector) {
  const input = document.querySelector(selector);
  if (!input) return;

  // Trouver le wrapper du datepicker
  const wrapper = input.closest(".date-input-wrapper");
  if (!wrapper) return;

  // Trouver l'input caché qui contient la valeur ISO
  const hiddenInput = wrapper.querySelector('input[type="hidden"]');
  if (!hiddenInput || !hiddenInput.value) return;

  // Trouver l'input visible et mettre à jour sa valeur
  const visibleInput = wrapper.querySelector('input[type="text"]');
  if (visibleInput) {
    const parsedDate = parseLocalDate(hiddenInput.value);
    visibleInput.value = formatDateForInput(parsedDate);
  }
}

function initDatepickers(selector) {
  const userLocale = getUserLocale();
  const airLocale = userLocale === "fr" ? localeFr : localeEn;

  document
    .querySelectorAll(
      typeof selector === "string"
        ? selector
        : 'input[type="date"]:not([data-datepicker-initialized]):not([data-datepicker-ignore="true"])'
    )
    .forEach((originalInput) => {
      if (originalInput.getAttribute("data-datepicker-ignore") === "true")
        return;
      originalInput.dataset.datepickerInitialized = "true";

      // Sauvegarder la valeur initiale
      if (originalInput.value) {
        originalInput.setAttribute("data-initial-date", originalInput.value);
      }

      const savedValue = originalInput.getAttribute("data-initial-date");

      const hiddenInput = originalInput.cloneNode(true);
      hiddenInput.type = "hidden";
      hiddenInput.value = savedValue || "";

      const visibleInput = document.createElement("input");
      visibleInput.type = "text";
      visibleInput.className = originalInput.className;
      visibleInput.placeholder =
        hiddenInput.placeholder ||
        (userLocale === "fr" ? "Entrez une date" : "Enter a date");
      visibleInput.readOnly = true;
      visibleInput.autocomplete = "off";
      visibleInput.style.paddingLeft = "48px";
      visibleInput.style.marginLeft = "-28px";

      // Icône calendrier
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("width", "20");
      svg.setAttribute("height", "20");
      svg.setAttribute("viewBox", "0 0 24 24");
      svg.innerHTML = `
      <g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5">
        <path d="M18 2v2M6 2v2m4 13v-3.653c0-.191-.137-.347-.305-.347H9m4.63 4l1.354-3.65c.063-.171-.071-.35-.263-.35H13"/>
        <path d="M2.5 12.243c0-4.357 0-6.536 1.252-7.89C5.004 3 7.02 3 11.05 3h1.9c4.03 0 6.046 0 7.298 1.354C21.5 5.707 21.5 7.886 21.5 12.244v.513c0 4.357 0 6.536-1.252 7.89C18.996 22 16.98 22 12.95 22h-1.9c-4.03 0-6.046 0-7.298-1.354C2.5 19.293 2.5 17.114 2.5 12.756zM6 8h12"/>
      </g>
    `;
      svg.style.position = "relative";
      svg.style.zIndex = 1;
      svg.style.transform = "translateX(16px)";

      // Bouton "✕" pour vider
      const clearBtn = document.createElement("button");
      clearBtn.type = "button";
      clearBtn.textContent = "✕";
      clearBtn.style.position = "absolute";
      clearBtn.style.right = "16px";
      clearBtn.style.zIndex = "2";
      clearBtn.style.background = "transparent";
      clearBtn.style.border = "none";
      clearBtn.style.cursor = "pointer";
      clearBtn.style.fontSize = "16px";
      clearBtn.style.color = "#666";
      clearBtn.style.padding = "0";
      clearBtn.style.lineHeight = "1";

      // Wrapper
      const wrapper = document.createElement("div");
      wrapper.className = "date-input-wrapper";
      wrapper.style.display = "flex";
      wrapper.style.alignItems = "center";
      wrapper.style.gap = "0.5rem";
      wrapper.style.position = "relative";

      let parsedDate = null;
      if (savedValue) {
        parsedDate = parseLocalDate(savedValue);

        // Formater manuellement pour le champ visible
        const formatter = new Intl.DateTimeFormat(
          userLocale === "fr" ? "fr-FR" : "en-US",
          {
            day: "2-digit",
            month: "long",
            year: "numeric",
          }
        );
        visibleInput.value = formatter.format(parsedDate);
      }

      wrapper.appendChild(svg);
      wrapper.appendChild(hiddenInput);
      wrapper.appendChild(visibleInput);
      wrapper.appendChild(clearBtn);

      originalInput.replaceWith(wrapper);

      const dp = new AirDatepicker(visibleInput, {
        locale: airLocale,
        autoClose: true,
        dateFormat: "dd MMMM yyyy",
        selectedDates: parsedDate ? [parsedDate] : [],
        onSelect({ date }) {
          hiddenInput.value = formatDateForServer(date);
          hiddenInput.setAttribute(
            "data-initial-date",
            formatDateForInput(date)
          );
          ["input", "change"].forEach((eventType) => {
            hiddenInput.dispatchEvent(new Event(eventType, { bubbles: true }));
          });
        },
      });

      // Vider la date
      clearBtn.addEventListener("click", () => {
        dp.clear();
        visibleInput.value = "";
        hiddenInput.value = "";
        hiddenInput.removeAttribute("data-initial-date");
        ["input", "change"].forEach((eventType) => {
          hiddenInput.dispatchEvent(new Event(eventType, { bubbles: true }));
        });
      });
    });
}

window.initDatepickers = initDatepickers;
window.updateDatepickerValue = updateDatepickerValue;

function initSlimSelects(selector) {
  Array.from(
    document.querySelectorAll(
      typeof selector === "string" ? selector : "select:not(.no-ss)"
    )
  )
    .filter((select) => {
      return !select.closest(".no-ss-group");
    })
    .forEach((select) => {
      // Détruire l'instance existante si elle existe
      // if (select.slim && typeof select.slim.destroy === 'function') {
      //   select.slim.destroy();
      //   delete select.slim;
      // }

      // Ne pas réinitialiser si déjà initialisé et que nous ne forçons pas
      // if (select.dataset.slimInitialized === 'true' && !selector) return;
      if (select.dataset.slimInitialized === "true") return;

      const isMultiple = select.multiple;

      // Récupérer le placeholder depuis data-placeholder ou l'option vide
      const placeholder = select.dataset.placeholder ||
                         (select.options[0]?.value === '' ? select.options[0]?.text : '') ||
                         '';

      // GO4S Design System : la recherche n'apparaît qu'au-delà de 7 options
      // (REFERENCE.md §11). Toujours active en multi-sélection.
      const showSearch = isMultiple || select.querySelectorAll("option").length > 7;

      const slimConfig = {
        select: select,
        allowDeselect: !isMultiple,
        closeOnSelect: isMultiple ? false : true,
        hideSelected: false,
        settings: {
          showSearch: showSearch,
          // Attache le menu déroulant au <body> (au lieu de l'inline dans le
          // wrapper) : il échappe ainsi à l'overflow des modales/tables et
          // s'affiche par-dessus l'overlay. Corrige les selects non cliquables
          // dans les modales DS.
          contentLocation: document.body,
          contentPosition: "fixed",
        },
        events: {
          afterChange: () => {
            select.dispatchEvent(new Event("change", { bubbles: true }));
            select.dispatchEvent(new Event("input", { bubbles: true }));
          },
        },
      };

      // Le placeholder du champ, sinon un texte traduit : jamais « Select Value ».
      const texts = SLIM_TEXTS[getUserLocale()];
      slimConfig.settings.placeholderText = placeholder || (isMultiple ? texts.multiple : texts.single);
      slimConfig.settings.searchPlaceholder = texts.search;
      slimConfig.settings.searchingText = texts.searching;
      slimConfig.settings.searchText = texts.none;

      // Stocker l'instance sur l'élément pour pouvoir y accéder plus tard
      select.slim = new SlimSelect(slimConfig);

      select.dataset.slimInitialized = "true";
    });
}
window.initSlimSelects = initSlimSelects;

function initializeNewElements() {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      setTimeout(() => {
        initSlimSelects();
        initDatepickers();
      }, 10);
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

const turboNavigationEvents = [
  "turbo:click", // Quand un lien est cliqué
  "turbo:before-visit", // Avant une navigation
  "turbo:visit", // Quand une navigation Turbo démarre
  "turbo:before-fetch-request", // Avant que Turbo envoie une requête
  "turbo:before-fetch-response", // Après réception de la réponse
  "turbo:submit-start", // Formulaire soumis
  "turbo:submit-end", // Réponse formulaire reçue
  "turbo:before-cache", // Avant que Turbo mette une page en cache
  "turbo:before-render", // Avant le rendu DOM (après fetch)
  "turbo:render", // Juste après mise à jour du DOM
  "turbo:load", // Quand la page est complètement chargée
];
const turboStreamEvents = [
  "turbo:before-stream", // Avant traitement d’un stream
  "turbo:before-stream-render", // Avant rendu d’un stream complet
  "turbo:before-stream-element", // Avant traitement d’un élément dans le stream
  "turbo:before-stream-insert", // Avant insertion
  "turbo:before-stream-append",
  "turbo:before-stream-prepend",
  "turbo:before-stream-replace",
  "turbo:before-stream-update",
  "turbo:before-stream-remove",
];

["DOMContentLoaded", ...turboNavigationEvents, ...turboStreamEvents].forEach(
  (eventName) => {
    document.addEventListener(eventName, initSlimSelects);
    document.addEventListener(eventName, initDatepickers);
    document.addEventListener(eventName, initializeNewElements);
  }
);

// Ensure Alpine.js is started for x- directives (only once)
let alpineStarted = false;
const startAlpine = () => {
  if (alpineStarted) return;
  try {
    Alpine.start();
    alpineStarted = true;
  } catch (e) {}
};

document.addEventListener("DOMContentLoaded", startAlpine);
document.addEventListener("turbo:load", startAlpine);

export { application };
